<script setup lang="ts">
import type { ForgotPasswordRequest } from '@/js/account/shared/model';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import signInImage from '@/images/account/sign-in.jpg';
import { AuthRouteName } from '@/js/account/app/router/route-names';
import AuthTitle from '@/js/account/components/auth/AuthTitle.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseField from '@/js/account/components/base/BaseField.vue';
import { accountForgotPassword } from '@/js/account/entities/auth';
import AuthLayout from '@/js/account/layouts/auth-layout/AuthLayout.vue';
import { useFormErrors } from '@/js/account/shared/lib';
import { classNames } from '@/js/lib/bem';

const router = useRouter();
const cn = classNames('forgot-password-page');

const {
  clearErrors,
  clearFieldOnInput,
  setFromAxiosError,
  fieldError,
  hasFieldError,
  generalError,
} = useFormErrors();

const form = reactive<ForgotPasswordRequest>({
  email: '',
});
const submitting = ref(false);

async function onSubmit(): Promise<void> {
  clearErrors();
  submitting.value = true;

  try {
    await accountForgotPassword({ email: form.email.trim() });
    await router.push({ name: AuthRouteName.CheckMail });
  }
  catch (error) {
    setFromAxiosError(error);
  }
  finally {
    submitting.value = false;
  }
}
</script>

<template>
  <AuthLayout :src="signInImage" alt="">
    <div :class="cn()">
      <AuthTitle :class="cn('title')" title="Forgot password">
        <template #description>
          <p :class="cn('description')">
            Email to send reset instructions to
          </p>
        </template>
      </AuthTitle>

      <form :class="cn('form')" @submit.prevent="onSubmit">
        <BaseField
          v-model="form.email"
          :class="cn('field')"
          label="Email"
          name="email"
          type="email"
          autocomplete="email"
          :invalid="hasFieldError('email') || !!generalError"
          :message="
            fieldError('email') || (generalError ? generalError : undefined)
          "
          required
          @update:model-value="
            clearFieldOnInput('email', { clearGeneral: true })
          "
        />

        <BaseButton
          :class="cn('button')"
          type="submit"
          full-width
          :loading="submitting"
        >
          Send
        </BaseButton>
      </form>
    </div>
  </AuthLayout>
</template>

<style lang="scss" scoped>
.forgot-password-page {
  max-width: 363px;
  width: 100%;

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__field {
    margin-bottom: 24px;
  }

  &__button {
    margin-top: 8px;
    margin-bottom: 40px;
  }

  &__title {
    text-align: center;
  }

  &__description {
    margin: 0;
    text-align: center;
    font-family: var(--font-family-sans);
    font-weight: var(--font-weight-regular);
    font-size: 16px;
    line-height: 1.125;
    color: var(--color-text-secondary);
  }
}
</style>
